import type { VorgangDto } from "../../../services/vorgangService";
import { deriveVorgangStatus, type VorgangStatusWert } from "./vorgangStatus";

/**
 * Statusfilter der Vorgangsliste und das lokale Nachziehen nach einem
 * Status-Wechsel (T-196, PO 06.10.2026).
 *
 * ══ ZWEI ACHSEN ═══════════════════════════════════════════════════════════
 * „Offen" und „Erledigt" sind der Status, „Importiert" ist die HERKUNFT aus
 * einer alten Kundenakte. Innerhalb des Status gilt ODER („Offen" + „Erledigt"
 * = alle), die Herkunft kommt per UND dazu: „Offen" + „Importiert" = die
 * offenen Importe — genau die Arbeitsliste, die man nach dem Kundenakten-
 * Import abhakt. Das Backend (`buildFeedFilters`) rechnet dasselbe; der Wert
 * geht komma-getrennt hin.
 *
 * T-244 (PO 12.10.2026): Auf der Herkunftsachse gibt es zusätzlich den
 * AUSSCHLUSS „OhneImport" („ohne importierte"), ebenfalls per UND mit dem
 * Status kombinierbar: „Offen" + „ohne importierte" = die offene Arbeit ohne
 * die Altakten-Importe. „nur importierte" und „ohne importierte" schließen
 * sich gegenseitig aus (`herkunftUmschalten`); steht trotzdem beides im Wert,
 * gilt die Herkunft als ungefiltert — wie im Backend.
 */

export const STATUS_OPTIONS = ["Offen", "Erledigt"] as const;
export const IMPORTIERT = "Importiert";
export const OHNE_IMPORT = "OhneImport";
/** Alle Werte in fester Reihenfolge — sie bestimmt den Refetch-Key. */
export const STATUS_FILTER_OPTIONS: string[] = [...STATUS_OPTIONS, IMPORTIERT, OHNE_IMPORT];

/** Den komma-getrennten Filterwert in seine Teile zerlegen. */
export const statusFilterWerte = (value: string | undefined | null): string[] =>
    (value ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

/**
 * Gehört eine Zeile mit diesem Status und dieser Herkunft noch in die Liste,
 * so wie sie gerade gefiltert ist? Spiegelt das Backend — damit eine erledigte
 * Zeile aus der Liste „offene Importe" verschwindet, ohne dass neu geladen wird.
 */
export function passtZumStatusFilter(
    status: string,
    importiert: boolean,
    filterwert: string | undefined | null,
): boolean {
    const teile = statusFilterWerte(filterwert);
    const stati = teile.filter((t) => t !== IMPORTIERT && t !== OHNE_IMPORT);
    if (stati.length > 0 && !stati.includes(status)) return false;
    const nur = teile.includes(IMPORTIERT);
    const ohne = teile.includes(OHNE_IMPORT);
    if (nur && !ohne && !importiert) return false;
    if (ohne && !nur && importiert) return false;
    return true;
}

/**
 * Eine Option im Statusfilter an- oder abschalten. Die beiden Herkunftswerte
 * („nur importierte" / „ohne importierte") schließen sich aus: wer den einen
 * wählt, verliert den anderen. Die Reihenfolge folgt `STATUS_FILTER_OPTIONS`,
 * damit der Wert (und der Refetch-Key) stabil bleibt. Leer → `undefined`.
 */
export function statusFilterUmschalten(
    filterwert: string | undefined | null,
    opt: string,
): string | undefined {
    const an = statusFilterWerte(filterwert);
    const aktiv = an.includes(opt);
    const gegenteil = opt === IMPORTIERT ? OHNE_IMPORT : opt === OHNE_IMPORT ? IMPORTIERT : null;
    const neu = STATUS_FILTER_OPTIONS.filter((o) => {
        if (o === opt) return !aktiv;
        if (!aktiv && o === gegenteil) return false;
        return an.includes(o);
    });
    return neu.length ? neu.join(",") : undefined;
}

/**
 * Ein Vorgang, wie ihn ein Neuladen nach dem Status-Wechsel liefern würde —
 * für das lokale Nachziehen der Liste (kein `reloadFeed()`, sonst springt die
 * Scroll-Position und die nachgeladenen Seiten sind weg).
 *
 * Spiegelt den Server (`VorgangService.update`):
 *  - **Erledigt**: alle aktiven To-dos werden mit erledigt, der Fortschritt ist
 *    voll. Beim ÜBERGANG leeren sich TWV und Frist (T-218) — am Vorgang und an
 *    den To-dos, die bis eben offen waren.
 *  - **Offen**: Galt der Vorgang nur wegen seiner erledigten To-dos als erledigt,
 *    öffnet der Server ALLE To-dos wieder (`reopenChildTodos`). Ohne To-dos
 *    zählt nur der Handstatus. Termine kommen nicht zurück.
 */
export function vorgangNachStatus(v: VorgangDto, neu: VorgangStatusWert): VorgangDto {
    const vorher = deriveVorgangStatus(v);
    const gesamt = v.fortschrittGesamt ?? 0;
    const todos = v.todos ?? [];
    if (neu === "Erledigt") {
        if (vorher === "Erledigt") return { ...v, statusManuell: "Erledigt", status: "Erledigt" };
        return {
            ...v,
            statusManuell: "Erledigt",
            status: "Erledigt",
            fortschrittErledigt: gesamt,
            twv: null as unknown as string,
            twvUhrzeit: null,
            frist: null as unknown as string,
            todos: todos.map((t) =>
                t.status === "Erledigt"
                    ? t
                    : { ...t, status: "Erledigt", twv: null as unknown as string, frist: null as unknown as string },
            ),
        };
    }
    // neu === "Offen"
    const durchTodosErledigt = gesamt > 0 && vorher === "Erledigt";
    if (!durchTodosErledigt) return { ...v, statusManuell: "Offen", status: gesamt > 0 ? vorher : "Offen" };
    return {
        ...v,
        statusManuell: "Offen",
        status: "Offen",
        fortschrittErledigt: 0,
        todos: todos.map((t) => (t.status === "Erledigt" ? { ...t, status: "Offen" } : t)),
    };
}
